import { Link as RouterLink, useLocation, useNavigate } from 'react-router';
import { Breadcrumbs, Link, LinkProps, Typography } from '@mui/material';
import { t } from 'i18next';

interface LinkRouterProps extends LinkProps {
  to: string;
}

function LinkRouter(props: LinkRouterProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <Link {...props} component={RouterLink as any} />;
}

// A single ancestor breadcrumb carried in navigation state. `to` is the
// destination (including any search string needed to restore that page) and
// `labelKey` is the i18n key for its label.
export interface BreadcrumbItem {
  labelKey: string;
  to: string;
}

// A breadcrumb resolved for rendering: an ancestor's label plus the state to
// re-supply when navigating back to it (the current page omits `state`).
interface RenderableCrumb {
  label: string;
  to: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  state?: any;
}

interface BreadcrumbTrailNavState {
  breadcrumbs?: BreadcrumbItem[];
  backTo?: string;
}

// Builds the breadcrumb trail from navigation state rather than the URL, so the
// crumbs reflect the path the user actually took. The Credit Card Activity
// screen, for example, lives at a single URL but shows different ancestors
// depending on whether it was reached from the Budget banner, a Spending
// Overview card, or the Home widget. `state.breadcrumbs` holds the ancestor
// crumbs; the current page (identified by `currentLabelKey`) is appended as the
// final, non-clickable crumb. Returns an empty trail when the route has no
// label, and the rendered component hides itself when only the current page is
// present (i.e. no ancestors).
export function useBreadcrumbCrumbs(currentLabelKey?: string): {
  crumbs: RenderableCrumb[];
  backTo?: string;
} {
  const location = useLocation();
  const state = location.state as BreadcrumbTrailNavState | null;
  const backTo = state?.backTo;

  if (!currentLabelKey) return { crumbs: [], backTo };

  const ancestors = state?.breadcrumbs ?? [];
  const crumbs: RenderableCrumb[] = ancestors.map((ancestor, index) => ({
    label: t(ancestor.labelKey),
    to: ancestor.to,
    // Clicking an ancestor restores it with only the crumbs that preceded it.
    state: { breadcrumbs: ancestors.slice(0, index) },
  }));
  crumbs.push({ label: t(currentLabelKey), to: `${location.pathname}${location.search}` });

  return { crumbs, backTo };
}

interface BreadcrumbTrailProps {
  crumbs: RenderableCrumb[];
  backTo?: string;
}

const BreadcrumbTrail: React.FC<BreadcrumbTrailProps> = ({ crumbs, backTo }) => {
  const navigate = useNavigate();

  // Only the current page in the trail -> nothing worth showing.
  if (crumbs.length <= 1) return <></>;

  return (
    <Breadcrumbs aria-label='breadcrumb'>
      {crumbs.map((crumb, index) => {
        const last = index === crumbs.length - 1;

        if (last) {
          // Current page: bold and non-interactive.
          return (
            <Typography key={crumb.to} variant='p16Bold' color='secondary'>
              {crumb.label}
            </Typography>
          );
        }

        return (
          <LinkRouter
            underline='hover'
            variant='p16'
            color='primary'
            to={crumb.to}
            onClick={(event) => {
              event.preventDefault();
              // When the immediate parent is the page we navigated from, step
              // back through history so its live state (filters, scroll) is
              // preserved instead of pushing a fresh instance.
              if (backTo && crumb.to === backTo) {
                navigate(-1);
                return;
              }
              navigate(crumb.to, { state: crumb.state });
            }}
            key={crumb.to}>
            {crumb.label}
          </LinkRouter>
        );
      })}
    </Breadcrumbs>
  );
};

export default BreadcrumbTrail;
